// M04 角、平行線、三角形與四邊形 - 主題 06-10 數據文件
// Angles, Parallel Lines, Triangles and Quadrilaterals - Topics 06-10
// 系統化設計，準備後續 Supabase 數據遷移

// M04-06: 三角不等式證明 (Triangle Inequality Proof)
export const M04_06_PROBLEMS = [
  {
    id: "M04_06_001",
    topicId: "M04_06",
    question: "在△ABC 中，證明三角不等式：AB + BC > AC。\n(a) 用幾何方法證明（延長線段法）\n(b) 用向量方法證明\n(c) 討論等號成立的條件",
    solution: "(a) 見幾何證明, (b) 見向量證明, (c) A、B、C 三點共線時等號成立",
    explanation: "三角不等式是幾何學的基本定理，有多種證明方法。",
    hint: "幾何法：在 AB 延長線上取點 D，使 BD = BC，證明 AC < AD。",
    difficulty: 3,
    order: 1,
    steps: [
      "(a) 延長AB到D，使BD = BC",
      "連接CD，則△BCD是等腰三角形",
      "∠BCD = ∠BDC",
      "∠ACD = ∠ACB + ∠BCD > ∠ACB",
      "在△ACD中，∠ACD > ∠ADC，∴ AD > AC",
      "而AD = AB + BD = AB + BC，∴ AB + BC > AC"
    ]
  },
  {
    id: "M04_06_002",
    topicId: "M04_06",
    question: "已知三角形三邊長為 a、b、c，證明：\n(a) |a - b| < c < a + b\n(b) a + b + c > 2max{a, b, c}\n(c) 若 a ≤ b ≤ c，則 c < (a + b + c)/2",
    solution: "(a) 見證明, (b) 見證明, (c) 見證明",
    explanation: "三角不等式的多種表述形式及其證明。",
    hint: "利用三角形任意兩邊之和大於第三邊的性質。",
    difficulty: 3,
    order: 2,
    steps: [
      "(a) 由 a + c > b 得 c > b - a",
      "由 b + c > a 得 c > a - b",
      "∴ c > |a - b|",
      "由 a + b > c 直接得到",
      "(b) 設 c = max{a, b, c}",
      "則 a + b > c，∴ a + b + c > 2c",
      "(c) 由 a + b > c 得 c < a + b < a + b + c",
      "∴ 2c < a + b + c，即 c < (a + b + c)/2"
    ]
  },
  {
    id: "M04_06_003",
    topicId: "M04_06",
    question: "利用三角不等式解決實際問題：\n(a) 證明：兩點之間直線距離最短\n(b) 在平面上，從點 A 到點 C，經過直線 l 上任意點 B，路徑長度的最小值\n(c) 設計一個物理或工程中的最短路徑問題",
    solution: "(a) 見證明, (b) 作 A 關於 l 的對稱點 A'，最短路徑為 A'C, (c) 見應用設計",
    explanation: "三角不等式在最優化問題中的應用。",
    hint: "利用反射原理和三角不等式的幾何意義。",
    difficulty: 4,
    order: 3,
    steps: [
      "(a) 對任意三點 A、B、C，有 AC ≤ AB + BC",
      "當且僅當 B 在線段 AC 上時等號成立",
      "∴ 直線距離最短",
      "(b) 作 A 關於直線 l 的對稱點 A'",
      "則 AB = A'B，路徑長度 = A'B + BC ≥ A'C",
      "當 A'、B、C 共線時取最小值 A'C",
      "(c) 應用：光的反射、聲波傳播路徑"
    ]
  },
  {
    id: "M04_06_004",
    topicId: "M04_06",
    question: "在空間中，證明四面體的邊長不等式：\n(a) 對四面體 ABCD，證明 AB + BC + CD > AD\n(b) 證明四面體任意四個面的周長之和大於所有邊長之和的 2 倍\n(c) 討論這些不等式的幾何意義",
    solution: "(a) 見證明, (b) 見證明, (c) 見幾何意義",
    explanation: "三角不等式在三維空間中的推廣。",
    hint: "利用平面三角不等式和空間幾何性質。",
    difficulty: 4,
    order: 4,
    steps: [
      "(a) 在平面 ABC 中，AB + BC > AC",
      "在△ACD 中，AC + CD > AD",
      "∴ AB + BC + CD > AC + CD > AD",
      "(b) 設六條邊為 a₁, a₂, ..., a₆",
      "四個面周長和 = 2(a₁ + a₂ + ... + a₆)",
      "由三角不等式可證",
      "(c) 表示空間中路徑的最優性"
    ]
  },
  {
    id: "M04_06_005",
    topicId: "M04_06",
    question: "利用三角不等式證明其他不等式：\n(a) 對實數 x、y，證明 |x + y| ≤ |x| + |y|\n(b) 證明 ||x| - |y|| ≤ |x - y|\n(c) 將這些結果推廣到 n 個實數的情況",
    solution: "(a) 見證明, (b) 見證明, (c) 見推廣",
    explanation: "三角不等式在數學分析中的應用。",
    hint: "將實數的絕對值類比為幾何中的距離。",
    difficulty: 4,
    order: 5,
    steps: [
      "(a) 考慮數軸上三點：0, x, x+y",
      "由三角不等式：|0-(x+y)| ≤ |0-x| + |x-(x+y)|",
      "即 |x+y| ≤ |x| + |y|",
      "(b) 由 |x| = |(x-y)+y| ≤ |x-y| + |y|",
      "得 |x| - |y| ≤ |x-y|",
      "同理 |y| - |x| ≤ |x-y|",
      "∴ ||x| - |y|| ≤ |x-y|"
    ]
  },
  {
    id: "M04_06_006",
    topicId: "M04_06",
    question: "創新證明問題：設計一個利用三角不等式的新證明。\n(a) 證明：在銳角三角形中，任意一邊小於另外兩邊之和的一半\n(b) 證明：三角形的中線長度小於對應邊的一半與另外兩邊平均值的和\n(c) 探討這些結果的實際應用價值",
    solution: "(a) 需要更精確的條件, (b) 見證明, (c) 見應用分析",
    explanation: "三角不等式的創新應用和推廣。",
    hint: "結合三角形的特殊性質和不等式技巧。",
    difficulty: 4,
    order: 6,
    steps: [
      "(a) 條件需要修正，銳角三角形中邊長關係更複雜",
      "(b) 設中線 ma 對應邊 a",
      "利用中線長度公式和三角不等式",
      "ma = (1/2)√(2b² + 2c² - a²)",
      "結合不等式技巧證明",
      "(c) 應用於結構設計、材料力學分析"
    ]
  }
];

// M04-07: 平行四邊形性質 (Parallelogram Properties)
export const M04_07_PROBLEMS = [
  {
    id: "M04_07_001",
    topicId: "M04_07",
    question: "在平行四邊形 ABCD 中，對角線 AC 和 BD 相交於點 O。\n(a) 證明 O 是 AC 和 BD 的中點\n(b) 若 AB = 5cm，AD = 8cm，∠BAD = 60°，求對角線長度\n(c) 計算平行四邊形的面積",
    solution: "(a) 見證明, (b) AC = 7cm, BD = √39cm, (c) 面積 = 20√3 cm²",
    explanation: "平行四邊形對角線互相平分是其重要性質。",
    hint: "利用平行四邊形對邊平行且相等的性質。",
    difficulty: 2,
    order: 1,
    steps: [
      "(a) ∵ AB ∥ CD 且 AB = CD",
      "∴ ∠BAO = ∠DCO（內錯角）",
      "∠ABO = ∠CDO（內錯角）",
      "∴ △AOB ≅ △COD（AAS）",
      "∴ AO = CO，BO = DO",
      "(b) 用餘弦定理：AC² = 5² + 8² - 2×5×8×cos60° = 39",
      "BD² = 5² + 8² - 2×5×8×cos120° = 129"
    ]
  },
  {
    id: "M04_07_002",
    topicId: "M04_07",
    question: "平行四邊形的判定定理應用：\n(a) 已知四邊形 PQRS 中，PQ ∥ SR 且 PQ = SR，證明 PQRS 是平行四邊形\n(b) 若四邊形對角線互相平分，證明它是平行四邊形\n(c) 設計一個實際測量問題來驗證四邊形是否為平行四邊形",
    solution: "(a) 見證明, (b) 見證明, (c) 見測量方案",
    explanation: "平行四邊形的多種判定方法及其應用。",
    hint: "利用平行四邊形的定義和性質進行相互轉換。",
    difficulty: 2,
    order: 2,
    steps: [
      "(a) 連接 PR",
      "∵ PQ ∥ SR 且 PQ = SR",
      "∴ 四邊形 PQRS 是平行四邊形（定義）",
      "(b) 設對角線交於點 O",
      "∵ AO = CO，BO = DO",
      "∴ △AOB ≅ △COD（SAS）",
      "∴ AB ∥ CD 且 AB = CD",
      "(c) 測量方案：測量對角線長度和交點位置"
    ]
  },
  {
    id: "M04_07_003",
    topicId: "M04_07",
    question: "平行四邊形中的角度關係：\n(a) 證明平行四邊形對角相等，鄰角互補\n(b) 在平行四邊形 ABCD 中，若∠A = 3∠B，求各角度數\n(c) 若對角線與邊的夾角為 30°，求平行四邊形各角度數",
    solution: "(a) 見證明, (b) ∠A = 135°, ∠B = 45°, (c) 需要更多條件",
    explanation: "平行四邊形內角的性質和計算方法。",
    hint: "利用平行線的性質和四邊形內角和。",
    difficulty: 3,
    order: 3,
    steps: [
      "(a) ∵ AB ∥ CD，∴ ∠A + ∠D = 180°（同側內角）",
      "同理 ∠B + ∠C = 180°",
      "又 ∠A = ∠C，∠B = ∠D（對角相等）",
      "(b) ∵ ∠A + ∠B = 180°，∠A = 3∠B",
      "∴ 3∠B + ∠B = 180°，∠B = 45°",
      "∠A = 135°",
      "(c) 需要指明是哪條對角線與哪條邊的夾角"
    ]
  },
  {
    id: "M04_07_004",
    topicId: "M04_07",
    question: "平行四邊形的面積計算：\n(a) 推導平行四邊形面積公式 S = ab sin θ\n(b) 已知平行四邊形兩鄰邊為 6cm 和 10cm，夾角為 45°，求面積\n(c) 若保持面積不變，夾角變為 90°，求新的邊長",
    solution: "(a) 見推導, (b) S = 30√2 cm², (c) 新邊長保持 6cm 和 10cm",
    explanation: "平行四邊形面積的多種計算方法。",
    hint: "面積等於底乘以對應的高。",
    difficulty: 2,
    order: 4,
    steps: [
      "(a) 以邊 a 為底，對應高為 h = b sin θ",
      "∴ S = a × h = ab sin θ",
      "(b) S = 6 × 10 × sin 45° = 60 × (√2/2) = 30√2",
      "(c) 當 θ = 90° 時，S = ab sin 90° = ab",
      "要保持面積不變：ab = 30√2",
      "若邊長仍為 6 和 10，則面積 = 60 ≠ 30√2",
      "需要調整邊長"
    ]
  },
  {
    id: "M04_07_005",
    topicId: "M04_07",
    question: "平行四邊形的向量表示：\n(a) 用向量證明平行四邊形對角線互相平分\n(b) 若 $\\vec{AB} = \\vec{a}$，$\\vec{AD} = \\vec{b}$，求 $\\vec{AC}$ 和 $\\vec{BD}$\n(c) 利用向量計算平行四邊形面積",
    solution: "(a) 見向量證明, (b) $\\vec{AC} = \\vec{a} + \\vec{b}$, $\\vec{BD} = \\vec{b} - \\vec{a}$, (c) $S = |\\vec{a} \\times \\vec{b}|$",
    explanation: "向量方法在平行四邊形性質研究中的應用。",
    hint: "利用向量加法的平行四邊形法則。",
    difficulty: 3,
    order: 5,
    steps: [
      "(a) 設 O 為對角線交點",
      "$\\vec{AO} = \\vec{AB} + \\vec{BO}$",
      "$\\vec{CO} = \\vec{CB} + \\vec{BO} = -\\vec{BC} + \\vec{BO}$",
      "由平行四邊形性質可證 $\\vec{AO} = \\vec{OC}$",
      "(b) $\\vec{AC} = \\vec{AB} + \\vec{BC} = \\vec{AB} + \\vec{AD} = \\vec{a} + \\vec{b}$",
      "$\\vec{BD} = \\vec{BA} + \\vec{AD} = -\\vec{a} + \\vec{b} = \\vec{b} - \\vec{a}$"
    ]
  },
  {
    id: "M04_07_006",
    topicId: "M04_07",
    question: "平行四邊形的實際應用設計：\n(a) 設計一個機械連桿機構，利用平行四邊形的性質\n(b) 在建築設計中，如何利用平行四邊形確保結構穩定？\n(c) 分析汽車懸掛系統中平行四邊形結構的作用",
    solution: "(a) 見機械設計, (b) 見建築應用, (c) 見懸掛分析",
    explanation: "平行四邊形在工程技術中的廣泛應用。",
    hint: "平行四邊形能保持運動軌跡平行和力的傳遞。",
    difficulty: 4,
    order: 6,
    steps: [
      "(a) 平行四邊形連桿機構：",
      "保證輸出桿與輸入桿平行運動",
      "廣泛應用於升降平台、天線調節等",
      "(b) 建築中的桁架結構",
      "利用平行四邊形分散荷載",
      "提高結構穩定性",
      "(c) 汽車懸掛系統：保持車輪垂直於地面"
    ]
  }
];

// M04-08: 特殊四邊形判定 (Special Quadrilateral Identification)
export const M04_08_PROBLEMS = [
  {
    id: "M04_08_001",
    topicId: "M04_08",
    question: "判定矩形的方法研究：\n(a) 證明：有一個角是直角的平行四邊形是矩形\n(b) 證明：對角線相等的平行四邊形是矩形\n(c) 已知四邊形 ABCD 中，∠A = ∠B = ∠C = 90°，證明它是矩形",
    solution: "(a) 見證明, (b) 見證明, (c) 見證明",
    explanation: "矩形的多種判定方法及其等價性。",
    hint: "矩形是特殊的平行四邊形，具有直角和對角線相等的性質。",
    difficulty: 2,
    order: 1,
    steps: [
      "(a) 設平行四邊形 ABCD 中 ∠A = 90°",
      "∵ 鄰角互補，∴ ∠B = 90°",
      "∵ 對角相等，∴ ∠C = ∠A = 90°，∠D = ∠B = 90°",
      "∴ ABCD 是矩形",
      "(b) 設平行四邊形 ABCD 中 AC = BD",
      "利用餘弦定理可證各角為 90°",
      "(c) 三個角為直角，第四個角也必為直角"
    ]
  },
  {
    id: "M04_08_002",
    topicId: "M04_08",
    question: "菱形的判定與性質：\n(a) 證明：四邊相等的四邊形是菱形\n(b) 證明：對角線垂直的平行四邊形是菱形\n(c) 在菱形 ABCD 中，若∠A = 60°，邊長為 6cm，求對角線長度",
    solution: "(a) 見證明, (b) 見證明, (c) AC = 6√3cm, BD = 6cm",
    explanation: "菱形的判定方法和對角線性質。",
    hint: "菱形的對角線垂直且互相平分。",
    difficulty: 3,
    order: 2,
    steps: [
      "(a) 四邊相等的四邊形必為平行四邊形",
      "且為特殊的平行四邊形——菱形",
      "(b) 設對角線垂直的平行四邊形 ABCD",
      "由對角線性質可證四邊相等",
      "(c) 在菱形中，∠A = 60°，則∠B = 120°",
      "利用菱形對角線性質：",
      "AC = 2 × 6 × sin(60°/2) = 6√3",
      "BD = 2 × 6 × cos(60°/2) = 6"
    ]
  },
  {
    id: "M04_08_003",
    topicId: "M04_08",
    question: "正方形的判定方法：\n(a) 證明：四邊相等且有一個直角的四邊形是正方形\n(b) 證明：對角線相等且垂直的平行四邊形是正方形\n(c) 設計一個實際測量方案來判定四邊形是否為正方形",
    solution: "(a) 見證明, (b) 見證明, (c) 見測量方案",
    explanation: "正方形的多重判定條件和實際應用。",
    hint: "正方形同時具有矩形和菱形的所有性質。",
    difficulty: 3,
    order: 3,
    steps: [
      "(a) 四邊相等 → 菱形",
      "有一個直角 → 所有角都是直角",
      "∴ 既是菱形又是矩形 → 正方形",
      "(b) 對角線相等 → 矩形",
      "對角線垂直 → 菱形",
      "∴ 既是矩形又是菱形 → 正方形",
      "(c) 測量方案：",
      "1. 測量四邊長度是否相等",
      "2. 測量一個內角是否為90°"
    ]
  },
  {
    id: "M04_08_004",
    topicId: "M04_08",
    question: "梯形的分類與判定：\n(a) 區分一般梯形、等腰梯形和直角梯形\n(b) 證明：兩腰相等的梯形是等腰梯形\n(c) 在等腰梯形 ABCD 中，AB ∥ CD，AB = 10cm，CD = 6cm，高為 4cm，求腰長",
    solution: "(a) 見分類, (b) 見證明, (c) 腰長 = 5cm",
    explanation: "梯形的分類標準和等腰梯形的性質。",
    hint: "等腰梯形具有軸對稱性。",
    difficulty: 2,
    order: 4,
    steps: [
      "(a) 一般梯形：一組對邊平行",
      "等腰梯形：兩腰相等的梯形",
      "直角梯形：有一個直角的梯形",
      "(b) 利用等腰梯形的對稱性質證明",
      "(c) 作高，形成直角三角形",
      "底邊差 = (10 - 6)/2 = 2cm",
      "腰長 = √(4² + 2²) = √20 = 2√5cm"
    ]
  },
  {
    id: "M04_08_005",
    topicId: "M04_08",
    question: "綜合判定問題：\n(a) 四邊形 ABCD 中，AC ⊥ BD，AO = CO，BO = DO，判定四邊形類型\n(b) 若再加條件 AC = BD，四邊形變為什麼類型？\n(c) 設計一個判定流程圖來識別特殊四邊形",
    solution: "(a) 菱形, (b) 正方形, (c) 見流程圖",
    explanation: "通過性質組合來判定特殊四邊形的類型。",
    hint: "按照性質的組合逐步縮小範圍。",
    difficulty: 3,
    order: 5,
    steps: [
      "(a) 對角線互相平分 → 平行四邊形",
      "對角線垂直 → 菱形",
      "(b) 對角線相等且垂直 → 正方形",
      "(c) 判定流程：",
      "1. 是否為四邊形？",
      "2. 對邊是否平行？→ 平行四邊形",
      "3. 是否有直角？→ 矩形",
      "4. 四邊是否相等？→ 菱形/正方形"
    ]
  },
  {
    id: "M04_08_006",
    topicId: "M04_08",
    question: "特殊四邊形的實際應用：\n(a) 分析建築中不同四邊形結構的優缺點\n(b) 設計一個利用特殊四邊形性質的工程問題\n(c) 討論在藝術設計中如何運用四邊形的美學特性",
    solution: "(a) 見建築分析, (b) 見工程設計, (c) 見藝術應用",
    explanation: "特殊四邊形在各個領域的實際應用價值。",
    hint: "不同四邊形具有不同的力學性質和美學效果。",
    difficulty: 4,
    order: 6,
    steps: [
      "(a) 建築應用分析：",
      "矩形：穩定性好，施工簡單",
      "菱形：具有動感，分散應力",
      "正方形：最穩定，經典美觀",
      "(b) 工程問題：橋樑桁架設計",
      "利用三角形和四邊形組合",
      "優化結構強度和材料使用",
      "(c) 藝術設計：幾何美學原理"
    ]
  }
];

// M04-09: 菱形對角線關係 (Rhombus Diagonal Relationships)
export const M04_09_PROBLEMS = [
  {
    id: "M04_09_001",
    topicId: "M04_09",
    question: "菱形對角線的基本性質：\n(a) 證明菱形的對角線互相垂直\n(b) 證明菱形的對角線互相平分\n(c) 若菱形邊長為 10cm，一條對角線長 12cm，求另一條對角線長",
    solution: "(a) 見證明, (b) 見證明, (c) 另一條對角線長 = 16cm",
    explanation: "菱形對角線的垂直平分性質是其重要特徵。",
    hint: "利用菱形四邊相等的性質和等腰三角形性質。",
    difficulty: 2,
    order: 1,
    steps: [
      "(a) 設菱形 ABCD，對角線交於 O",
      "∵ AB = AD（菱形性質）",
      "∴ △ABD 是等腰三角形",
      "∴ AO ⊥ BD（等腰三角形頂角平分線垂直於底邊）",
      "(b) 由平行四邊形性質直接得到",
      "(c) 設對角線為 2a 和 2b",
      "由勾股定理：a² + b² = 10²",
      "已知 2a = 12，∴ a = 6",
      "∴ b² = 100 - 36 = 64，b = 8",
      "另一條對角線 = 2b = 16cm"
    ]
  },
  {
    id: "M04_09_002",
    topicId: "M04_09",
    question: "菱形對角線與角度的關係：\n(a) 在菱形 ABCD 中，若對角線長度比為 3:4，求各內角度數\n(b) 證明菱形的對角線平分各內角\n(c) 若菱形一個內角為 120°，邊長為 6cm，求對角線長度",
    solution: "(a) 內角為 arctan(4/3) 和 180° - arctan(4/3), (b) 見證明, (c) 長對角線 = 6√3cm，短對角線 = 6cm",
    explanation: "菱形對角線與內角之間的密切關係。",
    hint: "利用菱形對角線垂直的性質和三角函數關係。",
    difficulty: 3,
    order: 2,
    steps: [
      "(a) 設對角線長為 6k 和 8k",
      "在直角三角形中，tan(∠/2) = 4/3",
      "∴ ∠/2 = arctan(4/3)，∠ = 2arctan(4/3)",
      "(b) 由菱形對稱性和等腰三角形性質",
      "對角線是各內角的角平分線",
      "(c) 內角 120°，則鄰角 60°",
      "利用菱形對角線性質計算"
    ]
  },
  {
    id: "M04_09_003",
    topicId: "M04_09",
    question: "菱形面積的多種計算方法：\n(a) 用對角線長度計算菱形面積公式：S = (1/2)d₁d₂\n(b) 用邊長和內角計算：S = a²sin θ\n(c) 驗證兩種方法的等價性",
    solution: "(a) S = (1/2) × 12 × 16 = 96cm², (b) S = 10² × sin θ = 96cm², (c) 見等價性證明",
    explanation: "菱形面積計算的多種方法及其相互關係。",
    hint: "利用菱形的幾何性質建立不同公式間的聯繫。",
    difficulty: 3,
    order: 3,
    steps: [
      "(a) 對角線將菱形分成四個直角三角形",
      "面積 = 4 × (1/2) × (d₁/2) × (d₂/2) = (1/2)d₁d₂",
      "(b) 菱形面積 = 底 × 高 = a × a sin θ = a²sin θ",
      "(c) 設對角線長為 2p, 2q，邊長為 a",
      "由勾股定理：p² + q² = a²",
      "由三角關係：2pq = a²sin θ",
      "∴ (1/2) × 2p × 2q = 2pq = a²sin θ"
    ]
  },
  {
    id: "M04_09_004",
    topicId: "M04_09",
    question: "菱形對角線的向量表示：\n(a) 設菱形的兩個鄰邊向量為 $\\vec{a}$ 和 $\\vec{b}$，且 $|\\vec{a}| = |\\vec{b}|$，求對角線向量\n(b) 證明 $\\vec{AC} \\perp \\vec{BD}$\n(c) 利用向量計算菱形面積",
    solution: "(a) $\\vec{AC} = \\vec{a} + \\vec{b}$, $\\vec{BD} = \\vec{b} - \\vec{a}$, (b) 見向量證明, (c) $S = |\\vec{a} \\times \\vec{b}|$",
    explanation: "用向量方法研究菱形對角線性質。",
    hint: "利用向量的數量積和叉積性質。",
    difficulty: 4,
    order: 4,
    steps: [
      "(a) 設 A 為原點，$\\vec{AB} = \\vec{a}$, $\\vec{AD} = \\vec{b}$",
      "$\\vec{AC} = \\vec{AB} + \\vec{BC} = \\vec{a} + \\vec{b}$",
      "$\\vec{BD} = \\vec{BA} + \\vec{AD} = -\\vec{a} + \\vec{b}$",
      "(b) $\\vec{AC} \\cdot \\vec{BD} = (\\vec{a} + \\vec{b}) \\cdot (\\vec{b} - \\vec{a})$",
      "$= \\vec{b} \\cdot \\vec{b} - \\vec{a} \\cdot \\vec{a} = |\\vec{b}|² - |\\vec{a}|² = 0$",
      "(c) $S = |\\vec{AC} \\times \\vec{BD}|/2$ 或 $S = |\\vec{a}||\\vec{b}|\\sin\\theta$"
    ]
  },
  {
    id: "M04_09_005",
    topicId: "M04_09",
    question: "菱形的內切圓和外接圓：\n(a) 證明菱形有內切圓當且僅當它是正方形\n(b) 討論菱形的外接圓存在條件\n(c) 計算邊長為 8cm，內角為 60° 的菱形的內切圓半徑",
    solution: "(a) 見證明, (b) 菱形為正方形時才有外接圓, (c) r = 2√3cm",
    explanation: "菱形與圓的關係及其特殊情況。",
    hint: "內切圓要求四邊形的對邊距離相等。",
    difficulty: 4,
    order: 5,
    steps: [
      "(a) 菱形有內切圓 ⟺ 對邊距離相等",
      "⟺ 所有邊到圓心距離相等",
      "⟺ 菱形是正方形",
      "(b) 外接圓存在 ⟺ 四個頂點共圓",
      "⟺ 對角互補 ⟺ 菱形是矩形 ⟺ 菱形是正方形",
      "(c) 面積 S = a²sin 60° = 64 × (√3/2) = 32√3",
      "周長 p = 4 × 8 = 32",
      "內切圓半徑 r = S/(p/2) = 32√3/16 = 2√3"
    ]
  },
  {
    id: "M04_09_006",
    topicId: "M04_09",
    question: "菱形在實際中的應用設計：\n(a) 設計一個基於菱形對角線性質的機械裝置\n(b) 分析菱形在建築結構中的力學優勢\n(c) 探討菱形在藝術和裝飾設計中的美學價值",
    solution: "(a) 見機械設計, (b) 見力學分析, (c) 見美學討論",
    explanation: "菱形的實際應用價值和設計意義。",
    hint: "菱形的對稱性和穩定性使其在多個領域有重要應用。",
    difficulty: 4,
    order: 6,
    steps: [
      "(a) 機械應用：汽車千斤頂",
      "利用菱形對角線垂直性質",
      "實現力的方向轉換",
      "(b) 建築結構：",
      "菱形桁架分散荷載均勻",
      "對角線垂直提供雙向支撐",
      "(c) 藝術設計：",
      "菱形具有動感和方向性",
      "在圖案設計中創造視覺韻律"
    ]
  }
];

// M04-10: 正多邊形角度 (Regular Polygon Angles)
export const M04_10_PROBLEMS = [
  {
    id: "M04_10_001",
    topicId: "M04_10",
    question: "正多邊形內角和外角的基本計算：\n(a) 推導正 n 邊形每個內角的計算公式\n(b) 計算正六邊形每個內角和外角的度數\n(c) 若正多邊形每個外角為 24°，求邊數",
    solution: "(a) 內角 = (n-2)×180°/n, (b) 內角 = 120°, 外角 = 60°, (c) n = 15",
    explanation: "正多邊形角度計算的基本公式和應用。",
    hint: "外角和恆為 360°，內角 + 外角 = 180°。",
    difficulty: 2,
    order: 1,
    steps: [
      "(a) n 邊形內角和 = (n-2) × 180°",
      "正 n 邊形每個內角 = (n-2) × 180° / n",
      "(b) 正六邊形：n = 6",
      "內角 = (6-2) × 180° / 6 = 120°",
      "外角 = 180° - 120° = 60°",
      "(c) 外角 = 360° / n = 24°",
      "∴ n = 360° / 24° = 15"
    ]
  },
  {
    id: "M04_10_002",
    topicId: "M04_10",
    question: "正多邊形的中心角和圓周角：\n(a) 推導正 n 邊形的中心角公式\n(b) 在正八邊形中，求從中心到各頂點的連線與邊的夾角\n(c) 證明正多邊形的中心角等於外角",
    solution: "(a) 中心角 = 360°/n, (b) 夾角 = 22.5°, (c) 見證明",
    explanation: "正多邊形的中心角性質及其與外角的關係。",
    hint: "中心角是圓心對正多邊形一邊所張的角。",
    difficulty: 2,
    order: 2,
    steps: [
      "(a) 正 n 邊形的 n 個中心角之和 = 360°",
      "每個中心角 = 360° / n",
      "(b) 正八邊形中心角 = 360° / 8 = 45°",
      "從中心到頂點的連線與邊的夾角 = 45° / 2 = 22.5°",
      "(c) 中心角 = 360° / n",
      "外角 = 360° / n",
      "∴ 中心角 = 外角"
    ]
  },
  {
    id: "M04_10_003",
    topicId: "M04_10",
    question: "正多邊形的對角線角度：\n(a) 在正五邊形 ABCDE 中，求∠CAD 的度數\n(b) 證明正五邊形的對角線與邊的夾角為 36°\n(c) 計算正十二邊形中，從一個頂點引出的對角線與相鄰邊的夾角",
    solution: "(a) ∠CAD = 36°, (b) 見證明, (c) 夾角 = 15°",
    explanation: "正多邊形內部對角線形成的角度關係。",
    hint: "利用正多邊形的對稱性和等腰三角形性質。",
    difficulty: 3,
    order: 3,
    steps: [
      "(a) 正五邊形內角 = (5-2) × 180° / 5 = 108°",
      "△ACD 是等腰三角形（AC = CD）",
      "∠CAD = ∠CDA = (180° - 108°) / 2 = 36°",
      "(b) 在正五邊形中，對角線與邊夾角計算",
      "利用等腰三角形和內角性質",
      "(c) 正十二邊形內角 = 150°",
      "對角線與邊夾角 = (180° - 150°) / 2 = 15°"
    ]
  },
  {
    id: "M04_10_004",
    topicId: "M04_10",
    question: "正多邊形的星形角度：\n(a) 在正五角星中，求每個尖角的度數\n(b) 證明正 n 角星的尖角公式：尖角 = 180°(n-4)/n\n(c) 設計一個正七角星，計算其尖角度數",
    solution: "(a) 尖角 = 36°, (b) 見證明, (c) 正七角星尖角 ≈ 77.14°",
    explanation: "正多邊形星形的角度計算和性質。",
    hint: "星形的尖角與原正多邊形的內角有密切關係。",
    difficulty: 4,
    order: 4,
    steps: [
      "(a) 正五角星的尖角",
      "在五角星的一個三角形中",
      "頂角 = 180° - 2 × 72° = 36°",
      "(b) 正 n 角星的構造原理",
      "每隔一個頂點連線形成星形",
      "尖角 = 180°(n-4)/n",
      "(c) 正七角星：n = 7",
      "尖角 = 180° × 3 / 7 ≈ 77.14°"
    ]
  },
  {
    id: "M04_10_005",
    topicId: "M04_10",
    question: "正多邊形的旋轉對稱性：\n(a) 證明正 n 邊形具有 n 重旋轉對稱性\n(b) 計算正八邊形繞中心旋轉多少度後與原圖重合\n(c) 分析正多邊形的軸對稱性與邊數的關係",
    solution: "(a) 見證明, (b) 45°的整數倍, (c) 見對稱性分析",
    explanation: "正多邊形的對稱性質及其角度特徵。",
    hint: "對稱性與正多邊形的角度結構密切相關。",
    difficulty: 3,
    order: 5,
    steps: [
      "(a) 正 n 邊形每個頂點都等價",
      "繞中心旋轉 360°/n 後圖形重合",
      "共有 n 個這樣的旋轉位置",
      "(b) 正八邊形旋轉角度 = 360°/8 = 45°",
      "旋轉 45°, 90°, 135°, ... 後重合",
      "(c) 對稱軸數量：",
      "n 為偶數：n 條對稱軸",
      "n 為奇數：n 條對稱軸"
    ]
  },
  {
    id: "M04_10_006",
    topicId: "M04_10",
    question: "正多邊形在自然和人工設計中的角度應用：\n(a) 分析蜂巢六邊形結構的角度優勢\n(b) 設計一個基於正多邊形的建築結構，說明角度選擇的原因\n(c) 探討正多邊形在工業設計中的應用實例",
    solution: "(a) 見蜂巢分析, (b) 見建築設計, (c) 見工業應用",
    explanation: "正多邊形角度性質在實際中的重要應用。",
    hint: "自然界和人工設計中的正多邊形都有其角度優勢。",
    difficulty: 4,
    order: 6,
    steps: [
      "(a) 蜂巢六邊形優勢：",
      "120° 內角最接近理想角度",
      "材料利用率最高，結構最穩定",
      "(b) 建築設計應用：",
      "八邊形建築：45° 外角便於施工",
      "十二邊形穹頂：30° 外角，接近圓形",
      "(c) 工業設計：",
      "螺母通常為六邊形：便於扳手操作",
      "鉛筆橫截面：防滾動，便於握持"
    ]
  }
]; 